/**
 * Settings → Notifications: the 9 owner-lifecycle-mail toggles
 * (owner_notification_preferences, 20260826090000). Opt-out model — a
 * missing row means every category is on, matching the default the edge
 * functions (owner-lifecycle-emails.ts's isOwnerNotificationEnabled) assume
 * when they find no row either, so an owner who never opens this tab keeps
 * getting every mail exactly as before this feature shipped.
 */
import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";

export const OWNER_NOTIFICATION_PREFERENCE_KEYS = [
  "portal_ready",
  "lease_signed",
  "deposit_received",
  "keys_handed_over",
  "move_in_protocol",
  "rent_received",
  "move_out_date_confirmed",
  "move_out_protocol",
  "keys_returned",
] as const;

export type OwnerNotificationPreferenceKey =
  (typeof OWNER_NOTIFICATION_PREFERENCE_KEYS)[number];

export type OwnerNotificationPreferences = Record<
  OwnerNotificationPreferenceKey,
  boolean
>;

const ALL_ON: OwnerNotificationPreferences = Object.fromEntries(
  OWNER_NOTIFICATION_PREFERENCE_KEYS.map((k) => [k, true])
) as OwnerNotificationPreferences;

function queryKey(userId: string | undefined) {
  return ["v2", "owner-notification-preferences", userId] as const;
}

export function useOwnerNotificationPreferences() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const prefsQuery = useQuery({
    queryKey: queryKey(user?.id),
    enabled: !!user?.id,
    queryFn: async (): Promise<OwnerNotificationPreferences> => {
      const { data, error } = await supabase
        .from("owner_notification_preferences")
        .select(OWNER_NOTIFICATION_PREFERENCE_KEYS.join(", "))
        .eq("user_id", user!.id)
        .maybeSingle();
      if (error) throw error;
      if (!data) return { ...ALL_ON };
      return OWNER_NOTIFICATION_PREFERENCE_KEYS.reduce((acc, key) => {
        acc[key] = data[key] !== false;
        return acc;
      }, {} as OwnerNotificationPreferences);
    },
  });

  const preferences = prefsQuery.data ?? ALL_ON;

  const allOn = useMemo(
    () => OWNER_NOTIFICATION_PREFERENCE_KEYS.every((k) => preferences[k]),
    [preferences]
  );

  const setPreference = useMutation({
    mutationFn: async (patch: Partial<OwnerNotificationPreferences>) => {
      if (!user?.id) throw new Error("Not signed in");
      const { error } = await supabase
        .from("owner_notification_preferences")
        .upsert({ user_id: user.id, ...patch }, { onConflict: "user_id" });
      if (error) throw error;
    },
    onMutate: async (patch) => {
      await queryClient.cancelQueries({ queryKey: queryKey(user?.id) });
      const previous = queryClient.getQueryData<OwnerNotificationPreferences>(
        queryKey(user?.id)
      );
      queryClient.setQueryData(queryKey(user?.id), {
        ...(previous ?? ALL_ON),
        ...patch,
      });
      return { previous };
    },
    onError: (_err, _patch, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey(user?.id), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKey(user?.id) });
    },
  });

  const toggle = (key: OwnerNotificationPreferenceKey, value: boolean) =>
    setPreference.mutate({ [key]: value });

  const toggleAll = (value: boolean) =>
    setPreference.mutate(
      Object.fromEntries(
        OWNER_NOTIFICATION_PREFERENCE_KEYS.map((k) => [k, value])
      )
    );

  return {
    preferences,
    allOn,
    isLoading: prefsQuery.isLoading,
    toggle,
    toggleAll,
    isSaving: setPreference.isPending,
  };
}
